import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ApolloError } from '@apollo/client';
import { GetTagsForContactPickerDocument, useGetTagsForContactPickerQuery } from './ContactPicker.graphql.generated';
import type { GetTagsForContactPickerQuery, GetTagsForContactPickerQueryVariables } from './ContactPicker.graphql.generated';
import { useGuestListMembers } from './useGuestListMembers';
import type { LabelIdsToPeopleIdsMap, LabelViewItem, PersonViewItem, PersonItemRender } from '../PeoplePicker/PeoplePicker';
import type { StoredSelectedPerson, HardcodedLabel, HardcodedLabelsMode } from '../PeoplePicker/usePeoplePickerButtonController';
import { loadable } from '@shared/core/loadableFromNamedExport';
import { useOverlayUnstable } from '@withjoy/joykit';
import { useId } from '@withjoy/joykit/hooks';
import { getInGroupPosition } from '@shared/utils/groupedListUtils';
import { useTranslation } from '@shared/core';
import type { DeliveryMethodValue, OutsideScopeCopy, PeoplePickerUseCase, PickerLabel } from '../PeoplePicker/PeoplePicker.types';
import { usePeopleContext } from '@shared/context/people';
import type { SmartAudienceOption } from '../PeoplePicker/smartAudience/smartAudience.types';
import { useSmartAudienceSubmit } from '../PeoplePicker/smartAudience/useSmartAudienceSubmit';
import { getPersonName, getAvatarInitials } from '@shared/components/PeopleListItem/PeopleListItem.utils';
import { useEventInfo } from '@shared/utils/eventInfo';
import { persistPreference, resolvePhoneChannel } from '@shared/utils/messaging/deliveryChannel';
import { isPersonDisabled, useSublabelText, scopeUserLabels } from '../PeoplePicker/PeoplePicker.utils';
import { useRefreshWhileOpen } from '../PeoplePicker/useRefreshWhileOpen';
import { addError } from '@shared/utils/logger';
import { PhoneNumberSanitizationStatus } from '@graphql/generated-base';

const PeoplePickerDialog = loadable(() => import('../PeoplePicker/PeoplePicker.dialog'));

/**
 * Projects a picker view item onto the shape the caller persists. Both the hydrate-on-load effect
 * and the submit path store the same selection, so they derive it here rather than each keeping
 * their own copy — `hasPhone` in particular has to be decided once, since `recomputeDeliveryFlags`
 * trusts it as already-validated.
 */
const toStoredSelectedPerson = (person: PersonViewItem): StoredSelectedPerson => ({
  id: person.id,
  personIdTag: person.personIdTag,
  full_name: person.labelText,
  email: person.email,
  phoneDisplay: person.phoneDisplay,
  hasPhone: person.hasPhoneNumber,
  phoneChannelE164: person.phoneChannelE164,
  firstName: person.firstName,
  lastName: person.lastName,
  personName: getPersonName(person),
  avatarInitials: getAvatarInitials(person),
  hasEmailDeliveryMethodSelected: person.hasEmailDeliveryMethodSelected,
  hasTextDeliveryMethodSelected: person.hasTextDeliveryMethodSelected,
});

type SubmitArgs = Readonly<{
  mergedSelectionPeople: ReadonlyArray<PersonViewItem>;
  preference?: DeliveryMethodValue;
  smartAudience: SmartAudienceOption | null;
}>;

type UseContactPickerControllerProps = Readonly<{
  title: React.ReactNode;
  eventId: string;
  onSelectedPeople?: (people: ReadonlyArray<StoredSelectedPerson>) => void;
  enableSmsSelection?: boolean;
  smsOnly?: boolean;
  emailOnly?: boolean;
  initialDeliveryMethod?: DeliveryMethodValue;
  allowUnreachableSelection?: boolean;
  preferenceStorageKey?: string;
  showDeliveryMethodSelector?: boolean;
  /** Resolve each guest's WhatsApp or SMS channel onto the view item (MSG-584). Pass the `useIsSmsEnabled` result. */
  showPhoneChannel?: boolean;
  headerBanner?: React.ReactNode;
  hardcodedLabels?: ReadonlyArray<HardcodedLabel>;
  hardcodedLabelsMode?: HardcodedLabelsMode;
  /** Wording for the outside-scope warning. Only reachable in `requiredScope` mode. */
  outsideScopeCopy?: OutsideScopeCopy;
  /** Copy for the guest list's select-all control in `requiredScope` mode. See PeoplePicker. */
  selectAllLabel?: string;
  personItemRender?: PersonItemRender;
  useCase: PeoplePickerUseCase;
  /** Smart tags to offer (MSG-245), from `useSmartAudiences`. */
  smartAudiences?: ReadonlyArray<SmartAudienceOption>;
}>;

type UseContactPickerControllerReturn = Readonly<{
  loading: boolean;
  error: ApolloError | undefined;
  selectedPeople: ReadonlyArray<StoredSelectedPerson>;
  startSelection: () => void;
  viewPeople: ReadonlyArray<PersonViewItem>;
  viewLabels: ReadonlyArray<LabelViewItem>;
}>;

export const useContactPickerController = ({
  eventId,
  enableSmsSelection = false,
  smsOnly = false,
  emailOnly = false,
  initialDeliveryMethod,
  allowUnreachableSelection = false,
  preferenceStorageKey,
  showDeliveryMethodSelector = false,
  showPhoneChannel = false,
  headerBanner,
  title,
  onSelectedPeople,
  hardcodedLabels,
  hardcodedLabelsMode = 'exclusive',
  outsideScopeCopy,
  selectAllLabel,
  personItemRender,
  useCase,
  smartAudiences,
}: UseContactPickerControllerProps): UseContactPickerControllerReturn => {
  const { tq } = useTranslation('peoplePickerButton');
  const getSublabelText = useSublabelText();
  const isRequiredScopeMode = !!hardcodedLabels && hardcodedLabelsMode === 'requiredScope';
  const { eventHandle = '' } = useEventInfo();

  const { selectedPeople, setSelectedPeople } = usePeopleContext();
  const { initialSmartAudienceId, applySmartAudience } = useSmartAudienceSubmit(smartAudiences);
  const [hasStartedSelection, setHasStartedSelection] = useState(false);

  const {
    data: tagsData,
    error: tagsError,
    loading: tagsLoading,
    client,
  } = useGetTagsForContactPickerQuery({
    variables: { eventId },
    skip: !eventId,
    batchMode: 'fast',
  });

  // Paginated to the end of the roster; `loading` holds until the last page lands (MGT-76).
  const { members, error: membersError, loading: membersLoading, complete: membersComplete, refresh: refreshMembers } = useGuestListMembers(eventId, { skip: !eventId });

  // Tags are read through the client, not `refetch`, for the reason given in usePeoplePickerButtonController.
  const refreshRoster = () => {
    if (!eventId) return;
    client
      .query<GetTagsForContactPickerQuery, GetTagsForContactPickerQueryVariables>({ query: GetTagsForContactPickerDocument, variables: { eventId }, fetchPolicy: 'network-only' })
      .catch(error => addError(error, { context: 'useContactPickerController.refreshRoster', eventId }));
    void refreshMembers();
  };

  const loading = tagsLoading || membersLoading;
  const error = tagsError || membersError;

  // Build a map from tagDefinitionId → array of contactIds (members that have that tag)
  const tagMembershipMap = useMemo(() => {
    const map: Record<string, Array<string>> = {};
    for (const member of members) {
      for (const tag of member.tags) {
        if (!map[tag.tagDefinitionId]) {
          map[tag.tagDefinitionId] = [];
        }
        map[tag.tagDefinitionId].push(member.contact.id);
      }
    }
    return map;
  }, [members]);

  // Mirrors usePeoplePickerButtonController's merge — same two modes, same reasoning. Both pickers
  // feed the same PeoplePicker dialog, so `requiredScope` behaves identically on either path.
  const apiLabels = useMemo<ReadonlyArray<PickerLabel>>(() => {
    const tags = tagsData?.getTagsByEvent ?? [];
    const userLabels = tags
      .map(tag => ({
        id: tag.id,
        display: tag.label,
        people: (tagMembershipMap[tag.id] ?? []).map(contactId => ({ id: contactId, personIdTag: contactId })),
      }))
      .filter(label => label.people.length > 0);

    if (!hardcodedLabels) return userLabels;
    if (!isRequiredScopeMode) return hardcodedLabels;

    return scopeUserLabels(hardcodedLabels, userLabels);
  }, [hardcodedLabels, isRequiredScopeMode, tagsData, tagMembershipMap]);

  // See the note on the same derivation in usePeoplePickerButtonController: an empty scope is not
  // applied, so requesting the mode is not the same as being in it.
  const isScopeActive = useMemo(() => apiLabels.some(label => label.isRequiredScope), [apiLabels]);

  const viewLabels = useMemo<ReadonlyArray<LabelViewItem>>(
    () =>
      apiLabels.map(label => {
        const isRequiredScope = !!label.isRequiredScope;
        return {
          id: label.id,
          // Tags start unchecked in scope mode so the picker opens on the unnarrowed scope.
          selected: isRequiredScope || (!isScopeActive && !!label.initiallySelected),
          notSelectable: isRequiredScope,
          isRequiredScope,
          labelText: label.display,
          count: label.people.length,
          disabled: label.people.length === 0,
          labelDescription: label.labelDescription,
        };
      }),
    [apiLabels, isScopeActive],
  );

  const labelIdsToPeopleIdsMap = useMemo<LabelIdsToPeopleIdsMap>(() => {
    const map: Record<string, Array<string>> = {};
    for (const label of apiLabels) {
      map[label.id] = label.people.map(person => person.id);
    }
    return map;
  }, [apiLabels]);

  const viewPeople = useMemo<ReadonlyArray<PersonViewItem>>(() => {
    // Group members by groups[0].groupId, falling back to contact.id for ungrouped members
    const groupsMap: Record<string, typeof members> = {};
    for (const member of members) {
      const groupKey = member.groups?.[0]?.groupId ?? member.contact.id;
      if (!groupsMap[groupKey]) {
        groupsMap[groupKey] = [];
      }
      groupsMap[groupKey].push(member);
    }

    // O(1) lookups instead of O(n) .some()/.find() per member
    const selectedIds = new Set(selectedPeople.flatMap(sp => [sp.id, sp.personIdTag].filter(Boolean) as string[]));
    const selectedByKey = new Map<string, StoredSelectedPerson>();
    for (const sp of selectedPeople) {
      const stored = sp as StoredSelectedPerson;
      if (sp.id) selectedByKey.set(sp.id, stored);
      if (sp.personIdTag) selectedByKey.set(sp.personIdTag, stored);
    }

    const result: Array<PersonViewItem> = [];
    for (const groupMembers of Object.values(groupsMap)) {
      for (let index = 0; index < groupMembers.length; index++) {
        const member = groupMembers[index];
        const activeGuestKey = member.guestKeys.find(gk => !gk.disabledAt)?.guestKey;
        const hasGuestKey = !!activeGuestKey;
        const hasEmail = !!member.contact.email;
        const phoneRow = member.contact.phoneNumber?.[0];
        const phoneNumber = phoneRow?.number;
        // Presence, not a frontend re-derivation of contact_service's sanitization. The raw `number`
        // is what the host typed (`(404) 555-1234`, `07911 123456`), which libphonenumber can't read
        // without a country, and mirroring the backend's parse here greyed out guests — UK, AU and DE
        // locals among them — that were selectable before. contact_service decides deliverability at
        // dispatch. The value still has to be persisted as `hasPhone`: `recomputeDeliveryFlags` falls
        // back to `hasValidPhone(phoneDisplay)` without it, which drops those same locals from the
        // text channel on the first delivery-method change. Juan, on #17023.
        const hasValidPhoneNumber = !!phoneNumber?.trim();
        // Informational only (MSG-396): contact_service's stored verdict, read as-is rather than
        // re-parsed here. It never feeds `isDisabled`, `hasPhoneNumber` or the delivery flags — the
        // backend still decides deliverability at dispatch — and `unknown` means not yet checked,
        // so only `invalid` warns.
        const phoneInvalid = hasValidPhoneNumber && phoneRow?.numberSanitizationStatus === PhoneNumberSanitizationStatus.invalid;
        // Display only, like `phoneInvalid`: it never feeds selection or the delivery flags. Left unset
        // with the gate off, so the rows render as before.
        const resolvedChannel = showPhoneChannel ? resolvePhoneChannel({ hasPhone: hasValidPhoneNumber, e164: phoneRow?.numberE164 }) : undefined;

        const isDisabled = isPersonDisabled({
          hasEmail,
          hasPhone: hasValidPhoneNumber,
          hasGuestKey,
          emailOnly,
          smsOnly,
          allowUnreachableSelection,
          enableSmsSelection,
          deliveryMethod: initialDeliveryMethod,
        });
        const selected = selectedIds.has(member.contact.id) || (activeGuestKey ? selectedIds.has(activeGuestKey) : false);
        const storedSelectedPerson = selectedByKey.get(member.contact.id) ?? (activeGuestKey ? selectedByKey.get(activeGuestKey) : undefined);

        result.push({
          id: member.contact.id,
          personIdTag: activeGuestKey,
          selected,
          labelText: [member.contact.firstName, member.contact.lastName].filter(name => !!name).join(' '),
          firstName: member.contact.firstName ?? undefined,
          lastName: member.contact.lastName ?? undefined,
          sublabelText: getSublabelText({ smsOnly, emailOnly, hasPhone: hasValidPhoneNumber, phoneInvalid, enableSmsSelection, hasEmail }),
          disabled: isDisabled,
          groupId: member.groups?.[0]?.groupId ?? member.contact.id,
          avatarName: getAvatarInitials({ firstName: member.contact.firstName, lastName: member.contact.lastName }),
          inGroupPosition: getInGroupPosition(index, groupMembers.length),
          email: member.contact.email || '',
          phoneDisplay: phoneNumber ?? undefined,
          hasPhoneNumber: hasValidPhoneNumber,
          phoneInvalid,
          phoneChannel: resolvedChannel?.channel,
          phoneCountryCode: resolvedChannel?.countryCode,
          phoneChannelE164: phoneRow?.numberE164 ?? undefined,
          hasEmailDeliveryMethodSelected: storedSelectedPerson?.hasEmailDeliveryMethodSelected ?? (selected && hasEmail),
          hasTextDeliveryMethodSelected: storedSelectedPerson?.hasTextDeliveryMethodSelected ?? (enableSmsSelection && selected && hasValidPhoneNumber),
        });
      }
    }

    return result;
  }, [enableSmsSelection, emailOnly, smsOnly, allowUnreachableSelection, initialDeliveryMethod, showPhoneChannel, members, selectedPeople, getSublabelText]);

  // Enrich the selection in place once the whole roster has loaded (MSG-642): `complete`, not the first
  // page, so guests past it survive. The functional updater keeps flags a setDeliveryMethod set after
  // viewPeople was computed. Runs once (ref), since viewPeople depends on selectedPeople.
  const hasHydratedRef = useRef(false);
  useEffect(() => {
    if (hasHydratedRef.current || !membersComplete) return;
    hasHydratedRef.current = true;

    const viewByKey = new Map<string, PersonViewItem>();
    for (const view of viewPeople) {
      viewByKey.set(view.id, view);
      if (view.personIdTag) viewByKey.set(view.personIdTag, view);
    }

    setSelectedPeople(prev => {
      if (!prev.length) return prev;
      const seen = new Set<string>();
      const enriched: Array<StoredSelectedPerson> = [];
      for (const person of prev) {
        const view = (person.id ? viewByKey.get(person.id) : undefined) ?? (person.personIdTag ? viewByKey.get(person.personIdTag) : undefined);
        if (!view?.personIdTag || seen.has(view.id)) continue;
        seen.add(view.id);
        enriched.push({
          ...toStoredSelectedPerson(view),
          hasEmailDeliveryMethodSelected: person.hasEmailDeliveryMethodSelected ?? view.hasEmailDeliveryMethodSelected,
          hasTextDeliveryMethodSelected: person.hasTextDeliveryMethodSelected ?? view.hasTextDeliveryMethodSelected,
        });
      }
      return enriched;
    });
  }, [membersComplete, viewPeople, setSelectedPeople]);

  // Use a ref so the overlay always calls the latest submit without needing it in dependencies
  const submitRef = useRef((_args: SubmitArgs) => {});
  submitRef.current = ({ mergedSelectionPeople, preference, smartAudience }) => {
    if (preferenceStorageKey && preference) {
      persistPreference(preferenceStorageKey, preference);
    }
    if (applySmartAudience(smartAudience)) {
      setHasStartedSelection(false);
      hidePeoplePicker();
      return;
    }
    const newSelectedPeople = mergedSelectionPeople.filter(person => person.selected && !person.disabled).map(toStoredSelectedPerson);
    if (onSelectedPeople) {
      onSelectedPeople(newSelectedPeople);
    }
    setSelectedPeople(newSelectedPeople);
    setHasStartedSelection(false);
    hidePeoplePicker();
  };

  const cancelRef = useRef(() => {});
  cancelRef.current = () => {
    setHasStartedSelection(false);
    hidePeoplePicker();
  };

  // Per-instance overlay key — see the matching comment in usePeoplePickerButtonController. A shared
  // literal key lets two mounted pickers clobber each other in the module-level overlay registry.
  const overlayKey = useId(undefined, 'contact_picker');

  useRefreshWhileOpen(hasStartedSelection, refreshRoster);

  const [showPeoplePicker, hidePeoplePicker] = useOverlayUnstable(
    {
      overlayKey,
      render: overlayProps => {
        if (hasStartedSelection) {
          return (
            <PeoplePickerDialog
              enableSmsSelection={enableSmsSelection}
              smsOnly={smsOnly}
              emailOnly={emailOnly}
              initialDeliveryMethod={initialDeliveryMethod}
              showDeliveryMethodSelector={showDeliveryMethodSelector}
              headerBanner={headerBanner}
              tagsTitle={hardcodedLabels && !isRequiredScopeMode ? tq`Filters` : tq`My Tags`}
              personItemRender={personItemRender}
              outsideScopeCopy={outsideScopeCopy}
              selectAllLabel={selectAllLabel}
              smartAudiences={smartAudiences}
              initialSmartAudienceId={initialSmartAudienceId}
              {...overlayProps}
            />
          );
        }

        return <></>;
      },
      props: {
        title,
        eventHandle,
        allPeople: viewPeople,
        allLabels: viewLabels,
        labelIdsToPeopleIdsMap,
        submit: (args: SubmitArgs) => submitRef.current(args),
        cancel: () => cancelRef.current(),
        useCase,
        hasStartedSelection,
      },
    },
    { dependencies: [viewPeople, viewLabels, hasStartedSelection, initialDeliveryMethod, smartAudiences, initialSmartAudienceId] },
  );

  const startSelection = useCallback(() => {
    setHasStartedSelection(true);
    showPeoplePicker();
  }, [showPeoplePicker]);

  return {
    loading,
    error,
    selectedPeople,
    startSelection,
    viewPeople,
    viewLabels,
  };
};
